import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, Trash2, AlertTriangle, Loader2, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  
  // For non-authenticated users
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);

  const content = {
    es: {
      title: 'Eliminar cuenta',
      subtitle: 'Esta acción es permanente e irreversible',
      warning: 'Al eliminar tu cuenta se borrarán permanentemente:',
      items: [
        'Tu perfil y datos de usuario',
        'Todas tus listas de tareas y tareas',
        'Todas tus listas de premios y premios',
        'Todas tus notificaciones',
        'Tus suscripciones a notificaciones push'
      ],
      note: 'Las listas compartidas con otros usuarios permanecerán, pero se eliminará tu acceso.',
      confirmLabel: 'Escribe "ELIMINAR" para confirmar:',
      confirmPlaceholder: 'ELIMINAR',
      deleteButton: 'Eliminar mi cuenta permanentemente',
      deleting: 'Eliminando...',
      cancel: 'Cancelar',
      back: 'Volver',
      success: '¡Cuenta eliminada!',
      successMessage: 'Tu cuenta y todos tus datos han sido eliminados permanentemente.',
      goHome: 'Ir al inicio',
      // Non-authenticated
      notLoggedIn: '¿No puedes acceder a tu cuenta?',
      notLoggedInDesc: 'Si no puedes iniciar sesión, puedes solicitar la eliminación de tu cuenta proporcionando tu email.',
      emailLabel: 'Email de tu cuenta',
      emailPlaceholder: 'tu@email.com',
      requestButton: 'Solicitar eliminación',
      requestSent: 'Solicitud enviada',
      requestSentDesc: 'Si existe una cuenta con este email, procesaremos tu solicitud de eliminación en un plazo máximo de 30 días.',
      or: 'o',
      loginToDelete: 'Iniciar sesión para eliminar ahora'
    },
    en: {
      title: 'Delete account',
      subtitle: 'This action is permanent and irreversible',
      warning: 'Deleting your account will permanently erase:',
      items: [
        'Your profile and user data',
        'All your task lists and tasks',
        'All your reward lists and rewards',
        'All your notifications',
        'Your push notification subscriptions'
      ],
      note: 'Shared lists with other users will remain, but your access will be removed.',
      confirmLabel: 'Type "DELETE" to confirm:',
      confirmPlaceholder: 'DELETE',
      deleteButton: 'Delete my account permanently',
      deleting: 'Deleting...',
      cancel: 'Cancel',
      back: 'Back',
      success: 'Account deleted!',
      successMessage: 'Your account and all your data have been permanently deleted.',
      goHome: 'Go to home',
      notLoggedIn: 'Can\'t access your account?',
      notLoggedInDesc: 'If you can\'t sign in, you can request account deletion by providing your email.',
      emailLabel: 'Your account email',
      emailPlaceholder: 'your@email.com',
      requestButton: 'Request deletion',
      requestSent: 'Request sent',
      requestSentDesc: 'If an account exists with this email, we will process your deletion request within 30 days.',
      or: 'or',
      loginToDelete: 'Sign in to delete now'
    },
    gl: {
      title: 'Eliminar conta',
      subtitle: 'Esta acción é permanente e irreversible',
      warning: 'Ao eliminar a túa conta borraranse permanentemente:',
      items: [
        'O teu perfil e datos de usuario',
        'Todas as túas listas de tarefas e tarefas',
        'Todas as túas listas de premios e premios',
        'Todas as túas notificacións',
        'As túas subscricións a notificacións push'
      ],
      note: 'As listas compartidas con outros usuarios permanecerán, pero eliminarase o teu acceso.',
      confirmLabel: 'Escribe "ELIMINAR" para confirmar:',
      confirmPlaceholder: 'ELIMINAR',
      deleteButton: 'Eliminar a miña conta permanentemente',
      deleting: 'Eliminando...',
      cancel: 'Cancelar',
      back: 'Volver',
      success: 'Conta eliminada!',
      successMessage: 'A túa conta e todos os teus datos foron eliminados permanentemente.',
      goHome: 'Ir ao inicio',
      notLoggedIn: 'Non podes acceder á túa conta?',
      notLoggedInDesc: 'Se non podes iniciar sesión, podes solicitar a eliminación da túa conta proporcionando o teu email.',
      emailLabel: 'Email da túa conta',
      emailPlaceholder: 'teu@email.com',
      requestButton: 'Solicitar eliminación',
      requestSent: 'Solicitude enviada',
      requestSentDesc: 'Se existe unha conta con este email, procesaremos a túa solicitude de eliminación nun prazo máximo de 30 días.',
      or: 'ou',
      loginToDelete: 'Iniciar sesión para eliminar agora'
    },
    ca: {
      title: 'Eliminar compte',
      subtitle: 'Aquesta acció és permanent i irreversible',
      warning: 'En eliminar el teu compte s\'esborraran permanentment:',
      items: [
        'El teu perfil i dades d\'usuari',
        'Totes les teves llistes de tasques i tasques',
        'Totes les teves llistes de premis i premis',
        'Totes les teves notificacions',
        'Les teves subscripcions a notificacions push'
      ],
      note: 'Les llistes compartides amb altres usuaris romandran, però s\'eliminarà el teu accés.',
      confirmLabel: 'Escriu "ELIMINAR" per confirmar:',
      confirmPlaceholder: 'ELIMINAR',
      deleteButton: 'Eliminar el meu compte permanentment',
      deleting: 'Eliminant...',
      cancel: 'Cancel·lar',
      back: 'Tornar',
      success: 'Compte eliminat!',
      successMessage: 'El teu compte i totes les teves dades han estat eliminats permanentment.',
      goHome: 'Anar a l\'inici',
      notLoggedIn: 'No pots accedir al teu compte?',
      notLoggedInDesc: 'Si no pots iniciar sessió, pots sol·licitar l\'eliminació del teu compte proporcionant el teu email.',
      emailLabel: 'Email del teu compte',
      emailPlaceholder: 'teu@email.com',
      requestButton: 'Sol·licitar eliminació',
      requestSent: 'Sol·licitud enviada',
      requestSentDesc: 'Si existeix un compte amb aquest email, processarem la teva sol·licitud d\'eliminació en un termini màxim de 30 dies.',
      or: 'o',
      loginToDelete: 'Iniciar sessió per eliminar ara'
    },
    zh: {
      title: '删除账户',
      subtitle: '此操作是永久且不可逆的',
      warning: '删除您的账户将永久删除：',
      items: [
        '您的个人资料和用户数据',
        '所有任务列表和任务',
        '所有奖励列表和奖励',
        '所有通知',
        '推送通知订阅'
      ],
      note: '与其他用户共享的列表将保留，但您的访问权限将被删除。',
      confirmLabel: '输入"DELETE"以确认：',
      confirmPlaceholder: 'DELETE',
      deleteButton: '永久删除我的账户',
      deleting: '删除中...',
      cancel: '取消',
      back: '返回',
      success: '账户已删除！',
      successMessage: '您的账户和所有数据已被永久删除。',
      goHome: '返回首页',
      notLoggedIn: '无法访问您的账户？',
      notLoggedInDesc: '如果您无法登录，可以通过提供您的电子邮件来请求删除账户。',
      emailLabel: '您的账户邮箱',
      emailPlaceholder: 'your@email.com',
      requestButton: '请求删除',
      requestSent: '请求已发送',
      requestSentDesc: '如果存在此电子邮件的账户，我们将在30天内处理您的删除请求。',
      or: '或',
      loginToDelete: '登录以立即删除'
    }
  };

  const c = content[lang] || content.es;
  const confirmWord = lang === 'en' || lang === 'zh' ? 'DELETE' : 'ELIMINAR';

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== confirmWord) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, { withCredentials: true });
      setDeleted(true);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error al eliminar la cuenta');
    } finally {
      setDeleting(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Por favor, introduce un email válido');
      return;
    }
    
    setSendingRequest(true);
    try {
      await axios.post(`${API}/auth/request-deletion`, { email });
      setRequestSent(true);
    } catch (error) {
      console.error('Request error:', error);
      toast.error('Error al enviar la solicitud');
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  // Success state
  if (deleted) {
    return (
      <div className="min-h-screen bg-[#FFF9F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border-2 border-[#18181B] rounded-2xl hard-shadow p-8 text-center">
          <div className="w-16 h-16 bg-[#ADE498] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#18181B]">
            <CheckCircle className="w-8 h-8 text-[#18181B]" />
          </div>
          <h1 className="text-2xl font-bold mb-2">{c.success}</h1>
          <p className="text-zinc-600 mb-6">{c.successMessage}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-full border-2 border-[#18181B] bg-[#18181B] text-white py-3 font-bold hover:bg-[#333] transition-colors"
          >
            {c.goHome}
          </button>
        </div>
      </div>
    );
  }

  // Request sent state (for non-authenticated users)
  if (requestSent) {
    return (
      <div className="min-h-screen bg-[#FFF9F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border-2 border-[#18181B] rounded-2xl hard-shadow p-8 text-center">
          <div className="w-16 h-16 bg-[#FFC247] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#18181B]">
            <Mail className="w-8 h-8 text-[#18181B]" />
          </div>
          <h1 className="text-2xl font-bold mb-2">{c.requestSent}</h1>
          <p className="text-zinc-600 mb-6">{c.requestSentDesc}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-full border-2 border-[#18181B] bg-[#18181B] text-white py-3 font-bold hover:bg-[#333] transition-colors"
          >
            {c.goHome}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F0] p-4 md:p-6" data-testid="delete-account-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </button>
          <LanguageSelector />
        </div>

        {/* Main Card */}
        <div className="bg-white border-2 border-[#18181B] rounded-2xl hard-shadow p-6 md:p-8">
          {/* Warning Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center border-2 border-red-300">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{c.title}</h1>
              <p className="text-sm text-red-600">{c.subtitle}</p>
            </div>
          </div>

          {isAuthenticated && user ? (
            // Authenticated user flow
            <>
              {/* User info */}
              <div className="bg-zinc-50 rounded-xl p-4 my-6 flex items-center gap-3">
                {user.picture && (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full border-2 border-zinc-200" />
                )}
                <div>
                  <p className="font-bold text-sm">{user.name}</p>
                  <p className="text-xs text-zinc-500">{user.email}</p>
                </div>
              </div>

              {/* Warning box */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-bold text-red-800">{c.warning}</p>
                </div>
                <ul className="space-y-1 ml-7">
                  {c.items.map((item, i) => (
                    <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-red-600 mt-3 ml-7 italic">{c.note}</p>
              </div>

              {!showConfirm ? (
                <button
                  onClick={() => setShowConfirm(true)}
                  className="w-full rounded-full border-2 border-red-600 bg-red-600 text-white py-3 font-bold hover:bg-red-700 transition-colors"
                  data-testid="show-confirm-btn"
                >
                  {c.deleteButton}
                </button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2">{c.confirmLabel}</label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                      placeholder={c.confirmPlaceholder}
                      className="w-full px-4 py-3 rounded-xl border-2 border-zinc-200 focus:border-red-500 focus:outline-none font-mono text-center text-lg"
                      data-testid="confirm-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowConfirm(false);
                        setConfirmText('');
                      }}
                      className="flex-1 rounded-full border-2 border-[#18181B] bg-white py-3 font-bold hover:bg-zinc-50 transition-colors"
                    >
                      {c.cancel}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={confirmText !== confirmWord || deleting}
                      className="flex-1 rounded-full border-2 border-red-600 bg-red-600 text-white py-3 font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="delete-btn"
                    >
                      {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {deleting ? c.deleting : c.deleteButton.split(' ')[0]}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Non-authenticated user flow
            <>
              <div className="my-6">
                <h2 className="font-bold mb-2">{c.notLoggedIn}</h2>
                <p className="text-sm text-zinc-600 mb-4">{c.notLoggedInDesc}</p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-bold mb-2">{c.emailLabel}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={c.emailPlaceholder}
                      className="w-full px-4 py-3 rounded-xl border-2 border-zinc-200 focus:border-[#18181B] focus:outline-none"
                      data-testid="email-input"
                    />
                  </div>
                  <button
                    onClick={handleRequestDeletion}
                    disabled={sendingRequest || !email}
                    className="w-full rounded-full border-2 border-red-600 bg-red-600 text-white py-3 font-bold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="request-deletion-btn"
                  >
                    {sendingRequest && <Loader2 className="w-4 h-4 animate-spin" />}
                    {c.requestButton}
                  </button>
                </div>

                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-zinc-200"></div>
                  <span className="text-sm text-zinc-400">{c.or}</span>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <button
                  onClick={() => navigate('/login')}
                  className="w-full rounded-full border-2 border-[#18181B] bg-white py-3 font-bold hover:bg-zinc-50 transition-colors"
                >
                  {c.loginToDelete}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Privacy link */}
        <p className="text-center text-xs text-zinc-400 mt-6">
          <button onClick={() => navigate('/privacy-policy')} className="hover:underline">
            {content[lang]?.title || 'Política de Privacidad'} →
          </button>
        </p>
      </div>
    </div>
  );
}
